import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Divider from '@mui/material/Divider';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RefreshIcon from '@mui/icons-material/Refresh';
import PersonIcon from '@mui/icons-material/Person';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SchoolIcon from '@mui/icons-material/School';
import DownloadIcon from '@mui/icons-material/Download';
import TranscriptViewer from '../../components/training/TranscriptViewer';
import DimensionScoreCard from '../../components/training/DimensionScoreCard';
import CompetencyRadar from '../../components/training/CompetencyRadar';
import TrainingSessionExportModal from '../../components/training/TrainingSessionExportModal';
import TrainingSessionAudioControls from '../../components/training/TrainingSessionAudioControls';
import TrainingSessionAudioStatus from '../../components/training/TrainingSessionAudioStatus';
import trainingApi from '../../services/trainingApi';
import { useTraining } from '../../context/TrainingContext';
import { verdictChip } from '../../utils/trainingVerdict';

const formatDate = (dateStr) => {
  if (!dateStr) return '--';
  return new Date(dateStr).toLocaleDateString('en-IE', {
    day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
};

const formatDuration = (seconds) => {
  if (!seconds) return '--';
  const mins = Math.floor(seconds / 60);
  if (mins < 60) return `${mins} min`;
  const hrs = Math.floor(mins / 60);
  const remainMins = mins % 60;
  return `${hrs}h ${remainMins}m`;
};

const LecturerSessionReview = () => {
  const { id: sessionId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { socket } = useTraining();
  // Caller (e.g. clinical supervisor's practice-sessions queue) can pass
  // location.state.from to override the default "back to /training/lecturer".
  const backTarget = location.state?.from || '/training/lecturer';
  const backLabel = location.state?.from ? 'Back' : 'Back';

  const [session, setSession] = useState(null);
  const [evaluation, setEvaluation] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [highlightedTurn, setHighlightedTurn] = useState(null);

  // Export modal state
  const [showExportModal, setShowExportModal] = useState(false);

  // Re-evaluate state
  const [methodologies, setMethodologies] = useState([]);
  const [selectedMethodologyId, setSelectedMethodologyId] = useState('');
  const [isReEvaluating, setIsReEvaluating] = useState(false);
  const [reEvalMessage, setReEvalMessage] = useState(null);

  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const [sessionRes, evalRes] = await Promise.all([
          trainingApi.lecturerGetSession(sessionId),
          trainingApi.lecturerGetEvaluation(sessionId).catch(() => null),
        ]);

        const _raw_sessionData = sessionRes.data || sessionRes; const sessionData = _raw_sessionData.data || _raw_sessionData;
        // Merge studentInfo and scenarioBrief into session for convenient access
        const sess = sessionData.session || sessionData;
        const studentInfo = sessionData.studentInfo;
        const scenarioBrief = sessionData.scenarioBrief;
        if (studentInfo?.name) sess.studentName = studentInfo.name;
        if (studentInfo?.email) sess.studentEmail = studentInfo.email;
        if (scenarioBrief?.name) sess.scenarioName = scenarioBrief.name;
        if (scenarioBrief) sess.scenarioBrief = scenarioBrief;
        setSession(sess);

        if (evalRes) {
          const _raw_evalData = evalRes.data || evalRes; const evalData = _raw_evalData.data || _raw_evalData;
          setEvaluation(evalData.evaluation || evalData);
        }
      } catch (err) {
        console.error('[LecturerSessionReview] Error loading session:', err);
        setError('Failed to load session data. You may not have permission to view this session.');
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [sessionId]);

  // Load available methodologies for re-evaluate selector
  useEffect(() => {
    trainingApi.listMethodologies().then(res => {
      const body = res.data || res;
      const data = body.data || body;
      setMethodologies(data.methodologies || data || []);
    }).catch(() => {});
  }, []);

  // Listen for live evaluation updates via socket
  useEffect(() => {
    if (!socket) return;

    const handleEvaluationComplete = async (data) => {
      if (data.sessionId === sessionId || data._id === sessionId) {
        // Reload full evaluation data
        try {
          const evalRes = await trainingApi.lecturerGetEvaluation(sessionId);
          const _raw = evalRes.data || evalRes;
          const evalData = _raw.data || _raw;
          setEvaluation(evalData.evaluation || evalData);
          setIsReEvaluating(false);
          setReEvalMessage({ severity: 'success', text: 'Re-evaluation complete.' });
          setTimeout(() => setReEvalMessage(null), 5000);
        } catch {
          setEvaluation(data.evaluation || data);
          setIsReEvaluating(false);
        }
      }
    };

    socket.on('training:evaluation_complete', handleEvaluationComplete);
    return () => socket.off('training:evaluation_complete', handleEvaluationComplete);
  }, [socket, sessionId]);

  const handleReEvaluate = useCallback(async () => {
    try {
      setIsReEvaluating(true);
      setReEvalMessage(null);
      await trainingApi.lecturerReEvaluate(sessionId, selectedMethodologyId || undefined);
      setEvaluation(null); // Clear current evaluation while re-running
      setReEvalMessage({ severity: 'info', text: 'Re-evaluation in progress... This typically takes 30-60 seconds.' });

      // Poll for completion since the lecturer may not be in the socket room
      const pollInterval = setInterval(async () => {
        try {
          const evalRes = await trainingApi.lecturerGetEvaluation(sessionId);
          const _raw = evalRes.data || evalRes;
          const evalData = _raw.data || _raw;
          const result = evalData.evaluation || evalData;
          if (result && result.status === 'completed') {
            clearInterval(pollInterval);
            setEvaluation(result);
            setIsReEvaluating(false);
            setReEvalMessage({ severity: 'success', text: 'Re-evaluation complete.' });
            setTimeout(() => setReEvalMessage(null), 5000);
          } else if (result && result.status === 'failed') {
            clearInterval(pollInterval);
            setIsReEvaluating(false);
            setReEvalMessage({ severity: 'error', text: result.errorMessage || 'Re-evaluation failed.' });
          }
        } catch { /* keep polling */ }
      }, 5000);

      // Safety timeout after 3 minutes
      setTimeout(() => {
        clearInterval(pollInterval);
        if (isReEvaluating) {
          setIsReEvaluating(false);
          setReEvalMessage({ severity: 'warning', text: 'Re-evaluation is taking longer than expected. Refresh the page to check results.' });
        }
      }, 180000);
    } catch (err) {
      setIsReEvaluating(false);
      setReEvalMessage({ severity: 'error', text: err.response?.data?.error || 'Failed to start re-evaluation.' });
    }
  }, [sessionId, selectedMethodologyId, isReEvaluating]);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
        <CircularProgress />
        <Typography variant="body1" sx={{ ml: 2 }}>Loading session review...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ maxWidth: 600, mx: 'auto', py: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/training/lecturer')}
        >
          Return to Lecturer Dashboard
        </Button>
      </Box>
    );
  }

  const transcript = session?.transcript || [];
  const compositeScore = evaluation?.compositeScore;
  const maxCompositeScore = evaluation?.maxCompositeScore || 100;
  const passFail = evaluation?.passFail;
  const dimensions = evaluation?.dimensions || [];
  const criticalFlags = evaluation?.criticalFlags || [];
  const formulation = session?.formulation;
  const reflection = session?.selfReflection;

  // Build competency radar data from dimensions
  const radarData = dimensions.filter(d => !d.missing && d.score != null).map(d => ({
    dimension: d.name,
    score: d.score,
    maxScore: d.maxScore,
  }));

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 120px)' }}>
      {/* Top bar with back button and summary */}
      <Paper
        sx={{
          p: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexShrink: 0,
          bgcolor: evaluation
            ? (passFail === 'pass' ? 'success.light' : passFail === 'fail' ? 'error.light' : 'grey.100')
            : 'grey.100',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button
            variant="text"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(backTarget)}
            size="small"
          >
            Back
          </Button>
          <Divider orientation="vertical" flexItem />
          <Box>
            <Typography variant="h6">{session?.scenarioName || session?._scenarioName || 'Training Session'}</Typography>
            <Typography variant="body2" color="text.secondary">
              Session Review (Read-Only)
            </Typography>
          </Box>
          <Button
            variant="outlined"
            size="small"
            startIcon={<DownloadIcon />}
            onClick={() => setShowExportModal(true)}
          >
            Export
          </Button>
        </Box>

        {/* Score + Re-evaluate controls */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {evaluation ? (
            <Box sx={{ textAlign: 'center' }}>
              <Typography
                variant="h3"
                fontWeight={700}
                color={passFail === 'pass' ? 'success.dark' : passFail === 'fail' ? 'error.dark' : 'text.primary'}
              >
                {compositeScore != null ? `${Math.round(compositeScore)}%` : '--'}
              </Typography>
              {passFail && (
                <Chip
                  label={verdictChip(passFail).label}
                  color={verdictChip(passFail).color}
                  size="small"
                />
              )}
              {evaluation?.evaluationMethodologyVersion && (
                <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                  v{evaluation.evaluationMethodologyVersion}
                </Typography>
              )}
            </Box>
          ) : (
            <Box sx={{ textAlign: 'center' }}>
              <CircularProgress size={32} sx={{ mb: 0.5 }} />
              <Typography variant="caption" display="block">
                {isReEvaluating ? 'Re-evaluating...' : 'Evaluation pending...'}
              </Typography>
            </Box>
          )}
          <Divider orientation="vertical" flexItem />
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <TextField
              select size="small" label="Methodology" value={selectedMethodologyId}
              onChange={e => setSelectedMethodologyId(e.target.value)}
              sx={{ minWidth: 200 }}
            >
              <MenuItem value="">Default (active)</MenuItem>
              {methodologies.map(m => (
                <MenuItem key={m._id} value={m._id}>
                  {m.name} {m.isActive ? '(active)' : ''} v{m.version}
                </MenuItem>
              ))}
            </TextField>
            <Button
              size="small" variant="outlined" startIcon={<RefreshIcon />}
              onClick={handleReEvaluate} disabled={isReEvaluating}
            >
              {isReEvaluating ? 'Re-evaluating...' : 'Re-evaluate'}
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* Re-evaluation status message */}
      {reEvalMessage && (
        <Alert severity={reEvalMessage.severity} sx={{ mx: 2, mt: 1 }} onClose={() => setReEvalMessage(null)}>
          {reEvalMessage.text}
        </Alert>
      )}

      {/* Audio Controls */}
      {transcript.length > 0 && (
        <Box sx={{ px: 2, pt: 1 }}>
          <TrainingSessionAudioControls
            transcript={transcript}
            sessionId={session?._id}
            voiceConfig={session?.scenarioBrief?.voiceConfig || {}}
          />
        </Box>
      )}

      {/* Audio Export Status (inline for current session) */}
      <TrainingSessionAudioStatus sessionId={session?._id} />

      {/* Export Modal */}
      <TrainingSessionExportModal
        open={showExportModal}
        onClose={() => setShowExportModal(false)}
        session={session}
        evaluation={evaluation}
      />

      {/* Two-panel layout */}
      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        {/* Left panel: Transcript */}
        <Box sx={{ flex: 1, overflow: 'auto', p: 2, borderRight: 1, borderColor: 'divider' }}>
          <TranscriptViewer
            transcript={transcript}
            aiAnnotations={dimensions.flatMap(d => d.evidence || [])}
            highlightedTurn={highlightedTurn}
            onTurnHover={setHighlightedTurn}
          />
        </Box>

        {/* Right panel: Student info + evaluation */}
        <Box sx={{ width: { md: 440, lg: 520 }, overflow: 'auto', p: 2, flexShrink: 0 }}>
          {/* Student information */}
          <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
              Session Details
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PersonIcon fontSize="small" color="action" />
                <Typography variant="body2">
                  {session?.studentName || session?._studentName || 'Unknown Student'}
                </Typography>
                {session?.studentEmail && (
                  <Typography variant="caption" color="text.secondary">
                    ({session.studentEmail})
                  </Typography>
                )}
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <SchoolIcon fontSize="small" color="action" />
                <Typography variant="body2">
                  Scenario: {session?.scenarioName || session?._scenarioName || '--'}
                </Typography>
                {(session?._scenarioDifficulty || session?.scenarioBrief?.difficulty) && (
                  <Chip
                    label={session?.scenarioBrief?.difficulty || session._scenarioDifficulty}
                    size="small"
                    sx={{ textTransform: 'capitalize' }}
                  />
                )}
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CalendarTodayIcon fontSize="small" color="action" />
                <Typography variant="body2">
                  {formatDate(session?.startedAt)}
                  {session?.completedAt ? ` - ${formatDate(session.completedAt)}` : ''}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AccessTimeIcon fontSize="small" color="action" />
                <Typography variant="body2">
                  Duration: {formatDuration(session?.durationSeconds)}
                  {' | '}Turns: {transcript.length}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <VisibilityIcon fontSize="small" color="action" />
                <Typography variant="body2">
                  Disclosure Level:{' '}
                </Typography>
                {(() => {
                  const vl = session?.visibilityLevel
                    || session?.aiEvaluation?.visibilityLevel
                    || session?.scenarioBrief?.visibilityLevel
                    || null;
                  return vl ? (
                    <Chip
                      label={
                        vl === 'blind' ? 'Blind'
                        : vl === 'referral_only' ? 'Referral Only'
                        : vl === 'full_brief' ? 'Full Brief'
                        : vl
                      }
                      size="small"
                      color={
                        vl === 'blind' ? 'error'
                        : vl === 'referral_only' ? 'warning'
                        : 'success'
                      }
                      variant="outlined"
                    />
                  ) : (
                    <Typography variant="body2" color="text.secondary">Not set</Typography>
                  );
                })()}
              </Box>
              {session?.mode && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Mode: <strong style={{ textTransform: 'capitalize' }}>{session.mode.replace(/_/g, ' ')}</strong>
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Therapist-practice: AI feedback release control */}
            {session?.mode === 'therapist_practice' && (
              <Box sx={{ mt: 2, p: 2, bgcolor: 'info.lighter', borderRadius: 1, border: '1px solid', borderColor: 'info.light' }}>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>AI feedback visibility</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Visibility:&nbsp;<strong>{session.feedbackVisibility || 'supervisor_gated'}</strong>
                  {session.aiEvaluationReleased && session.aiEvaluationReleasedAt && (
                    <> · released {new Date(session.aiEvaluationReleasedAt).toLocaleString()}</>
                  )}
                </Typography>
                {session.feedbackVisibility === 'supervisor_gated' && (
                  <Button
                    size="small"
                    variant="contained"
                    disabled={!!session.aiEvaluationReleased}
                    onClick={async () => {
                      try {
                        const trainingApi = (await import('../../services/trainingApi')).default;
                        await trainingApi.releaseAiEvaluation(sessionId);
                        // refresh by reloading session
                        window.location.reload();
                      } catch (err) {
                        alert('Release failed: ' + (err?.response?.data?.error || err?.message));
                      }
                    }}
                  >
                    {session.aiEvaluationReleased ? 'AI feedback released' : 'Release AI feedback to therapist'}
                  </Button>
                )}
                {session.feedbackVisibility === 'never' && (
                  <Typography variant="caption" color="text.secondary">
                    Therapist will not see AI scores for this session.
                  </Typography>
                )}
                {session.feedbackVisibility === 'auto' && (
                  <Typography variant="caption" color="text.secondary">
                    Therapist sees AI scores immediately — no release needed.
                  </Typography>
                )}
              </Box>
            )}
          </Paper>

          {/* Evaluation Details */}
          {evaluation && evaluation.status === 'completed' && (
            <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                Evaluation Details
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Typography variant="body2">
                  Rubric:{' '}
                  <strong>
                    {evaluation.methodologyName
                      ? `${evaluation.methodologyName}${evaluation.methodologyOrganisation ? ` (${evaluation.methodologyOrganisation})` : ''} v${evaluation.evaluationMethodologyVersion || 1}`
                      : evaluation.reviewerPersonaName
                        ? `${evaluation.reviewerPersonaName} (legacy reviewer)`
                        : 'Default dimensions'
                    }
                  </strong>
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Model: {evaluation.modelUsed || '--'}
                  {evaluation.latencyMs ? ` | Evaluated in ${Math.round(evaluation.latencyMs / 1000)}s` : ''}
                </Typography>
                {evaluation.evaluatedAt && (
                  <Typography variant="caption" color="text.secondary">
                    Evaluated: {formatDate(evaluation.evaluatedAt)}
                  </Typography>
                )}
              </Box>
            </Paper>
          )}

          {/* Evaluation Section */}
          {!evaluation ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <CircularProgress sx={{ mb: 2 }} />
              <Typography variant="body1" color="text.secondary">
                Evaluation in progress...
              </Typography>
              <Typography variant="caption" color="text.secondary">
                This page will update automatically when the evaluation completes.
              </Typography>
            </Box>
          ) : (
            <>
              {/* Critical flags */}
              {criticalFlags.length > 0 && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" fontWeight={600}>Critical Flags</Typography>
                  {criticalFlags.map((flag, idx) => (
                    <Typography key={idx} variant="body2">
                      - {typeof flag === 'string' ? flag : `${flag.dimension || 'Unknown'}: ${flag.description || ''} (${flag.severity || ''})`}
                    </Typography>
                  ))}
                </Alert>
              )}

              {/* Competency Overview */}
              {radarData.length > 0 && (
                <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                  <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                    Competency Overview
                  </Typography>
                  <CompetencyRadar data={radarData} size="full" />
                </Paper>
              )}

              {/* Dimension scores */}
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                Dimension Scores
              </Typography>
              {dimensions.map((dim) => (
                <DimensionScoreCard
                  key={dim.key || dim.name}
                  dimension={dim}
                  onEvidenceClick={(turnIndex) => setHighlightedTurn(turnIndex)}
                />
              ))}

              {/* Strengths */}
              {evaluation.strengthsSummary && (
                <Paper variant="outlined" sx={{ p: 2, mb: 1.5, borderColor: 'success.main' }}>
                  <Typography variant="subtitle2" fontWeight={600} color="success.main" gutterBottom>
                    Strengths
                  </Typography>
                  <Typography variant="body2">{evaluation.strengthsSummary}</Typography>
                </Paper>
              )}

              {/* Areas for improvement */}
              {evaluation.improvementSummary && (
                <Paper variant="outlined" sx={{ p: 2, mb: 1.5, borderColor: 'warning.main' }}>
                  <Typography variant="subtitle2" fontWeight={600} color="warning.main" gutterBottom>
                    Areas for Improvement
                  </Typography>
                  <Typography variant="body2">{evaluation.improvementSummary}</Typography>
                </Paper>
              )}

              {/* Formulation viewer */}
              <Paper variant="outlined" sx={{ p: 2, mb: 1.5 }}>
                <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                  Student Formulation
                </Typography>
                {formulation ? (
                  <Box>
                    {typeof formulation === 'string' ? (
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                        {formulation}
                      </Typography>
                    ) : (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {formulation.presentingComplaint && (
                          <Box>
                            <Typography variant="caption" fontWeight={600}>Presenting Complaint:</Typography>
                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{formulation.presentingComplaint}</Typography>
                          </Box>
                        )}
                        {formulation.historyOfPresentingComplaint && (
                          <Box>
                            <Typography variant="caption" fontWeight={600}>History of Presenting Complaint:</Typography>
                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{formulation.historyOfPresentingComplaint}</Typography>
                          </Box>
                        )}
                        {formulation.backgroundHistory && (
                          <Box>
                            <Typography variant="caption" fontWeight={600}>Background History:</Typography>
                            {typeof formulation.backgroundHistory === 'string' ? (
                              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{formulation.backgroundHistory}</Typography>
                            ) : (
                              <Box sx={{ pl: 1 }}>
                                {formulation.backgroundHistory.psychiatric && <Typography variant="body2"><strong>Psychiatric:</strong> {formulation.backgroundHistory.psychiatric}</Typography>}
                                {formulation.backgroundHistory.medical && <Typography variant="body2"><strong>Medical:</strong> {formulation.backgroundHistory.medical}</Typography>}
                                {formulation.backgroundHistory.family && <Typography variant="body2"><strong>Family:</strong> {formulation.backgroundHistory.family}</Typography>}
                                {formulation.backgroundHistory.social && <Typography variant="body2"><strong>Social:</strong> {formulation.backgroundHistory.social}</Typography>}
                                {formulation.backgroundHistory.substance && <Typography variant="body2"><strong>Substance:</strong> {formulation.backgroundHistory.substance}</Typography>}
                              </Box>
                            )}
                          </Box>
                        )}
                        {formulation.mentalStateExamination && (
                          <Box>
                            <Typography variant="caption" fontWeight={600}>Mental State Examination:</Typography>
                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{formulation.mentalStateExamination}</Typography>
                          </Box>
                        )}
                        {formulation.riskAssessment && (
                          <Box>
                            <Typography variant="caption" fontWeight={600}>Risk Assessment:</Typography>
                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{formulation.riskAssessment}</Typography>
                          </Box>
                        )}
                        {formulation.differentialDiagnosis?.length > 0 && (
                          <Box>
                            <Typography variant="caption" fontWeight={600}>Differential Diagnosis:</Typography>
                            {formulation.differentialDiagnosis.map((dx, i) => (
                              <Typography key={i} variant="body2" sx={{ pl: 1 }}>
                                {dx.rank}. <strong>{dx.diagnosis}</strong>{dx.reasoning ? ` — ${dx.reasoning}` : ''}
                              </Typography>
                            ))}
                          </Box>
                        )}
                        {formulation.managementPlan && (
                          <Box>
                            <Typography variant="caption" fontWeight={600}>Management Plan:</Typography>
                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{formulation.managementPlan}</Typography>
                          </Box>
                        )}
                      </Box>
                    )}
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No formulation submitted for this session.
                  </Typography>
                )}
              </Paper>

              {/* Self-reflection viewer */}
              <Paper variant="outlined" sx={{ p: 2, mb: 1.5 }}>
                <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                  Student Self-Reflection
                </Typography>
                {reflection ? (
                  <Box>
                    {typeof reflection === 'string' ? (
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                        {reflection}
                      </Typography>
                    ) : (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {reflection.whatWentWell && (
                          <Box>
                            <Typography variant="caption" fontWeight={600}>What Went Well:</Typography>
                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{reflection.whatWentWell}</Typography>
                          </Box>
                        )}
                        {(reflection.whatWouldChange || reflection.whatToDoDifferently) && (
                          <Box>
                            <Typography variant="caption" fontWeight={600}>What I Would Do Differently:</Typography>
                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{reflection.whatToDoDifferently || reflection.whatWouldChange}</Typography>
                          </Box>
                        )}
                        {(reflection.keyLearning || reflection.uncertainties) && (
                          <Box>
                            <Typography variant="caption" fontWeight={600}>{reflection.keyLearning ? 'Key Learning:' : 'Uncertainties:'}</Typography>
                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{reflection.keyLearning || reflection.uncertainties}</Typography>
                          </Box>
                        )}
                        {reflection.confidenceLevel != null && (
                          <Box>
                            <Typography variant="caption" fontWeight={600}>
                              Confidence Level: {reflection.confidenceLevel}/10
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    )}
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No self-reflection submitted for this session.
                  </Typography>
                )}
              </Paper>

              {/* Phase 2 placeholder */}
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  mb: 1.5,
                  bgcolor: 'grey.50',
                  borderStyle: 'dashed',
                }}
              >
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Lecturer Feedback Tools
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Score override, annotations, written feedback, and release controls coming in Phase 2.
                </Typography>
              </Paper>
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default LecturerSessionReview;
